import { Box, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { ToggleableAnglesVideoPlayer } from "components/scrapEyeDataDashboard/VideoPlayer/components/ToggleableAnglesVideoPlayer";
import { VideoControls } from "components/scrapEyeDataDashboard/VideoPlayer/components/VideoControls";
import { HotkeyProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import {
  useVideoControl,
  VideoControlProvider,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { ProvideScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useEffect, useMemo, useState } from "react";
import { PLAYBACK_SCOPE } from "components/scrapEyeDataDashboard/VideoPlayer/constants";

const BUFFER_MINUTES = 10;

type PreviewBoundary = "start" | "end";

export const ExceptionVideoPreview = ({
  location,
  startedAt,
  endedAt,
}: {
  location: string;
  startedAt: Dayjs | null;
  endedAt: Dayjs | null;
}) => {
  const { t } = useTenantTranslation();
  const [previewBoundary, setPreviewBoundary] =
    useState<PreviewBoundary | null>(null);

  const hasRange = startedAt !== null && endedAt !== null;

  const videoWindow = useMemo(() => {
    if (!previewBoundary || startedAt === null || endedAt === null) return null;
    const anchor = previewBoundary === "start" ? startedAt : endedAt;
    return {
      start: anchor.subtract(BUFFER_MINUTES, "minute"),
      end: anchor.add(BUFFER_MINUTES, "minute"),
    };
  }, [previewBoundary, startedAt, endedAt]);

  return (
    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
      <ToggleButtonGroup
        value={previewBoundary}
        exclusive
        onChange={(_event, value: PreviewBoundary | null) => {
          setPreviewBoundary(value);
        }}
        disabled={!hasRange}
        size="small"
      >
        <ToggleButton value="start">{t("previewStart")}</ToggleButton>
        <ToggleButton value="end">{t("previewEnd")}</ToggleButton>
      </ToggleButtonGroup>

      {videoWindow && previewBoundary ? (
        <ProvideScrapEyeMetadata>
          <HotkeyProvider>
            <VideoControlProvider
              key={`${location}-${previewBoundary}-${videoWindow.start.toISOString()}`}
              autoPlay={false}
            >
              <SeekToOffset offsetSeconds={BUFFER_MINUTES * 60} />
              <Box sx={{ maxHeight: "50vh" }}>
                <ToggleableAnglesVideoPlayer
                  hotkeyScope={PLAYBACK_SCOPE}
                  storageKey="exceptional-status-preview"
                  location={location}
                  startIso={videoWindow.start.utc().toISOString()}
                  endIso={videoWindow.end.utc().toISOString()}
                  videoStart={videoWindow.start.utc()}
                  showVideoControls={false}
                />
              </Box>
              <VideoControls videoStart={videoWindow.start.utc()} />
            </VideoControlProvider>
          </HotkeyProvider>
        </ProvideScrapEyeMetadata>
      ) : null}
    </Stack>
  );
};

const SeekToOffset = ({ offsetSeconds }: { offsetSeconds: number }) => {
  const { actions } = useVideoControl();
  useEffect(() => {
    actions.seekTo(offsetSeconds);
  }, [actions, offsetSeconds]);
  return null;
};
